<template lang="pug">
    .coupon(:class="{ active, disabled, 'has-payout': isPayoutEligible, 'can-delete': canDelete }")
        el-button(
            v-if="!isPayoutEligible"
            :plain="active === false"
            size="small"
            type="info"
            :icon="active ? 'el-icon-fa-check fas' : 'el-icon-fa-circle far'"
            @click.prevent.stop="toggle"
        )
            span {{ label }}
            i.el-icon-fa-times.fas.delete-icon(v-if="active && canDelete")
        el-button(
            v-else
            :plain="active === false"
            size="small"
            type="info"
            :icon="active ? 'el-icon-fa-check fas' : 'el-icon-fa-circle far'"
            @click.prevent.stop="toggle"
        )
            span.coupon-name {{ label }}
            span.coupon-amount(v-if="coupon.name") {{ coupon.amount | moneyFormat(currencyCode) }}
            i.el-icon-fa-times.fas.delete-icon(v-if="active && canDelete")
</template>

<i18n>
{
    "en": {
        "{percentage}% on the entire purchase": "{percentage}% on the entire purchase",
        "Discount: {discount}": "Discount: {discount}"
    },
    "de": {
        "{percentage}% on the entire purchase": "{percentage}% auf den gesamten Einkauf",
        "Discount: {discount}": "Rabatt: {discount}"
    }
}
</i18n>

<script>
import { useSetupStore, usePosSetupStore, usePosCartStore } from '@/stores';
import { moneyFormat } from '@/filters';

import { get } from '@/helpers';

export default {
    name: 'Coupon',
    props: {
        coupon: {
            type: Object,
            required: true
        }
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms
        },
        posSetupStore() {
            return usePosSetupStore();
        },
        posCartStore() {
            return usePosCartStore();
        },
        purchaser() {
            return this.posCartStore.purchaser;
        },
        currency() {
            return this.posSetupStore.currency;
        },
        coupons() {
            return this.posCartStore.coupons;
        },
        applicableCustomerCoupons() {
            return this.posCartStore.applicableCustomerCoupons;
        },
        currencyCode() {
            return _.get(this.currency, 'iso4217') || 'EUR'
        },
        currencySymbol() {
            return _.get(this.currency, 'sign') || '€'
        },
        label() {
            let label = this.coupon.name;

            if (!label) {
                switch (this.coupon.type) {
                    case 'percentage':
                        label = this.$t('{percentage}% on the entire purchase', { percentage: this.coupon.percentage })
                        break;

                    default:
                    case 'fixed': {
                        label = this.$t('Discount: {discount}', { discount: moneyFormat(this.coupon.amount, this.currencyCode) })
                        break;
                    }
                }
            }

            return label;
        },
        active: {
            get() {
                let active = false;
                if (this.coupons.length) {
                    active = !!this.coupons.find(el => el.id === this.coupon.id && !el.disabled);
                }
                return active;
            },
            set(value) {
                if (value === true) this.posCartStore.addCoupon(this.coupon);
                else this.posCartStore.removeCouponFromCart(this.coupon);
                this.posCartStore.save();
            }
        },
        disabled() {
            let disabled = false;
            if (!this.active && this.coupons.length) {
                disabled = !!this.coupons.find(el => {
                    if (el.id === this.coupon.id) return;

                    let el_pos = (el.position || Infinity) + parseFloat(`0.${el.id}`);
                    let coupon_pos = (this.coupon.position || Infinity) + parseFloat(`0.${this.coupon.id}`);

                    return el_pos <= coupon_pos && el.discard_the_following_coupons;
                });
            }
            return disabled;
        },
        isPayoutEligible() {
            // Criteria for payout eligible coupon:
            // - type: fixed
            // - discard_remaining_amount === false
            // - max_uses === 1 || max_uses_per_customer >= 1
            return this.coupon.type === 'fixed' &&
                this.coupon.discard_remaining_amount === false &&
                (this.coupon.max_uses === 1 || (this.coupon.max_uses_per_customer && this.coupon.max_uses_per_customer >= 1));
        },
        canDelete() {
            // Can delete if NOT a customer auto-applied coupon
            // Customer auto-applied = code === null AND in applicableCustomerCoupons
            const isCustomerAutoApplied = this.coupon.code === null &&
                this.applicableCustomerCoupons.some(el => el.id === this.coupon.id);
            return !isCustomerAutoApplied;
        }
    },
    methods: {
        toggle() {
            this.active = !this.active;
            this.$emit('close-popover')
        }
    }
};
</script>

<style lang="sass" scoped>
.coupon
    &.has-payout
        &.active
            .coupon-amount
                background: rgba(255, 255, 255, 0.3)
                color: #fff

    .coupon-name
        white-space: nowrap

    .coupon-amount
        font-size: 10px
        font-weight: 600
        margin-left: 6px
        background: rgba(96, 98, 102, 0.15)
        color: #606266
        padding: 2px 6px
        border-radius: 8px

    .delete-icon
        margin-left: 6px
        opacity: 0.7
        &:hover
            opacity: 1
</style>
